"use client";

import {
  Box,
  Button,
  Container,
  Flex,
  Heading,
  Icon,
  Link,
  Stack,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";
import { ReactElement } from "react";
import { FcGlobe, FcLibrary, FcShipped } from "react-icons/fc";
import { FaMapMarkerAlt } from "react-icons/fa";

import Layout from "../../layout/Layout";
import { ExternalLinkIcon } from "@chakra-ui/icons";

interface CardProps {
  heading: string;
  description: string;
  icon: ReactElement;
  cardHref: string;
  apiList: string[];
  apiHrefList: string[];
}

const Card = ({
  heading,
  description,
  icon,
  cardHref,
  apiHrefList,
  apiList,
}: CardProps) => {
  return (
    <Box
      maxW={{ base: "full", md: "275px" }}
      w={"full"}
      borderWidth="1px"
      borderRadius="lg"
      overflow="hidden"
      shadow={"xl"}
      bg={"white"}
      p={5}
    >
      <Box
        as="a"
        href={cardHref}
        onClick={(e: { stopPropagation: () => any }) => e.stopPropagation()}
      >
        <Stack align={"start"} spacing={2}>
          <Flex
            w={16}
            h={16}
            align={"center"}
            justify={"center"}
            color={"white"}
            rounded={"full"}
            bg={useColorModeValue("gray.100", "gray.700")}
          >
            {icon}
          </Flex>
          <Box mt={2}>
            <Heading size="md">{heading}</Heading>
            <Text mt={1} fontSize={"sm"}>
              {description}
            </Text>
          </Box>
          <Stack align={"start"} textDecoration={"underline"}>
            {apiList.map((item, index) => (
              <Button
                as={Link}
                href={apiHrefList[index]}
                target={
                  apiHrefList[index] &&
                  (apiHrefList[index].startsWith("https") ||
                    apiHrefList[index].startsWith("http"))
                    ? "_blank"
                    : ""
                }
                key={index}
                variant="link"
                colorScheme="black"
                size="sm"
                onClick={(e) => e.stopPropagation()} // Prevents cardHref navigation
              >
                {item}{" "}
                {apiHrefList[index] &&
                  (apiHrefList[index].startsWith("https") ||
                    apiHrefList[index].startsWith("http")) && (
                    <>
                      &nbsp; <ExternalLinkIcon />
                    </>
                  )}
              </Button>
            ))}
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
};

export default function Homepage() {
  return (
    <Layout
      children={
        <Box p={4}>
          <Stack spacing={4} as={Container} maxW={"3xl"} textAlign={"center"}>
            <Heading fontSize={{ base: "2xl", sm: "4xl" }} fontWeight={"bold"}>
              Potters Maps API Playground
            </Heading>
            <Text color={"gray.600"} fontSize={{ base: "sm", sm: "lg" }}>
              Our APIs empower developers to build innovative mapping and
              location-based solutions.
            </Text>
          </Stack>

          <Container maxW={"5xl"} mt={12}>
            <Flex flexWrap="wrap" gridGap={6} justify="center">
              <Card
                heading={"Places APIs"}
                icon={<Icon as={FcGlobe} w={10} h={10} />}
                description={"Acquire accurate geographic information"}
                cardHref="/places"
                apiHrefList={["/places/autocomplete", "/places/detail"]}
                apiList={["Autocomplete", "Place detail"]}
              />
              <Card
                heading={"Geocode APIs"}
                icon={<Icon as={FaMapMarkerAlt} w={10} h={10} color="black" />}
                description={"Acquire accurate geographic information"}
                cardHref="/geocode"
                apiHrefList={["/geocode/forward", "/geocode/reverse"]}
                apiList={["Forward", "Reverse"]}
              />
              <Card
                heading={"Address APIs"}
                icon={<Icon as={FcLibrary} w={10} h={10} />}
                description={"Generate and derive address components"}
                cardHref="/address"
                apiHrefList={[
                  "/address/address-parsing",
                  "/address/address-validation",
                ]}
                apiList={["Address Parsing", "Address Validation"]}
              />
              <Card
                heading={"Route APIs"}
                icon={<Icon as={FcShipped} w={10} h={10} />}
                description={"Streamline your delivery and logistics solutions"}
                cardHref="/route"
                apiHrefList={[
                  "https://maponomy2.potterstech.com/api/planner/frontend/v7.html",
                ]}
                apiList={["Route Planner V3"]}
              />
            </Flex>
          </Container>
        </Box>
      }
    />
  );
}
